// src/components/AuthAction.jsx
//
// Shared Firebase email-action handler for AskLawly.
//
// Firebase Console custom Action URL:
//   https://www.asklawly.ai/auth/action
//
// Supported modes:
//   - verifyEmail
//   - resetPassword
//
// Signup verification flow is cross-device safe:
//   1. Firebase validates/applies the oobCode from the email link.
//   2. Firebase supplies the verified email from the action code.
//   3. This page calls AskLawly's load-balanced backend:
//        POST ${REACT_APP_BACKEND_URL}/auth/complete-signup
//   4. The backend looks up the Firebase user, confirms email_verified,
//      obtains the authoritative Firebase UID, reads pending_signups/{uid},
//      and creates the permanent profile + billing documents.
//
// No cookie, localStorage value, Firebase UID, or original signup device is
// required for authoritative completion. localStorage is only cleaned up when
// it happens to exist on the current device.

import React, { useEffect, useRef, useState } from "react";
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  getAuth,
  verifyPasswordResetCode,
} from "firebase/auth";
import { Link } from "react-router-dom";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const FRONTEND_URL = (process.env.REACT_APP_FRONTEND_URL || "").replace(/\/$/, "");

function cleanEmail(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function emailFromContinueUrl(value) {
  if (!value) return "";

  try {
    const parsed = new URL(value, window.location.origin);
    return cleanEmail(parsed.searchParams.get("email"));
  } catch (_err) {
    return "";
  }
}

function firebaseActionErrorMessage(error, fallback) {
  const code = error?.code || "";

  if (
    code === "auth/expired-action-code" ||
    code === "auth/invalid-action-code"
  ) {
    return "This email link is invalid, expired, or has already been used.";
  }

  if (code === "auth/user-disabled") {
    return "This account has been disabled.";
  }

  if (code === "auth/user-not-found") {
    return "The account associated with this email link could not be found.";
  }

  if (code === "auth/weak-password") {
    return "Please choose a stronger password.";
  }

  return fallback;
}

async function readJsonResponse(response) {
  try {
    return await response.json();
  } catch (_err) {
    return {};
  }
}

function isStrongPassword(value) {
  return (
    value.length >= 8 &&
    /[A-Z]/.test(value) &&
    /[a-z]/.test(value) &&
    /[0-9]/.test(value) &&
    /[!@#$%^&*(),.?":{}|<>]/.test(value)
  );
}

async function completeAskLawlySignup(email) {
  if (!BACKEND_URL) {
    throw new Error("REACT_APP_BACKEND_URL is not configured.");
  }

  const normalizedEmail = cleanEmail(email);
  if (!normalizedEmail) {
    throw new Error("Verified email address is unavailable.");
  }

  const response = await fetch(`${BACKEND_URL}/auth/complete-signup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email: normalizedEmail }),
  });

  const data = await readJsonResponse(response);

  if (!response.ok || data?.success === false) {
    const err = new Error(
      data?.error || "Email was verified, but signup could not be completed."
    );
    err.status = response.status;
    err.payload = data;
    throw err;
  }

  return data;
}

async function resendAskLawlyVerification(email) {
  if (!BACKEND_URL) {
    throw new Error("REACT_APP_BACKEND_URL is not configured.");
  }

  if (!FRONTEND_URL) {
    throw new Error("REACT_APP_FRONTEND_URL is not configured.");
  }

  const normalizedEmail = cleanEmail(email);
  if (!normalizedEmail) {
    throw new Error("Email address is unavailable.");
  }

  const emailActionLink =
    `${FRONTEND_URL}/complete-signup?email=${encodeURIComponent(normalizedEmail)}`;

  const response = await fetch(`${BACKEND_URL}/auth/resend-verification`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: normalizedEmail,
      email_action_link: emailActionLink,
    }),
  });

  const data = await readJsonResponse(response);

  if (!response.ok || data?.success === false) {
    const err = new Error(
      data?.error || "We could not send a new verification email."
    );
    err.status = response.status;
    err.payload = data;
    throw err;
  }

  return data;
}

const AuthAction = () => {
  const auth = getAuth();
  const startedRef = useRef(false);

  const [mode, setMode] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Processing your request...");
  const [error, setError] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [verificationResending, setVerificationResending] = useState(false);
  const [oobCode, setOobCode] = useState("");

  useEffect(() => {
    // Prevent duplicate one-time Firebase action execution if React re-runs
    // effects during development/StrictMode.
    if (startedRef.current) return;
    startedRef.current = true;

    let active = true;

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const actionMode = params.get("mode") || "";
      const actionCode = params.get("oobCode") || "";
      const continueUrl = params.get("continueUrl") || "";

      // The direct email parameter is not authoritative. It exists only as a
      // retry/finalization hint for signup links whose Firebase action code was
      // already consumed before the backend completion call succeeded.
      const fallbackEmail = cleanEmail(params.get("email")) || emailFromContinueUrl(continueUrl);

      if (!active) return;

      setMode(actionMode);
      setOobCode(actionCode);

      // Keep the email carried in the continue URL only as a recovery hint.
      // The backend still verifies Firebase Auth + pending_signups before
      // resending or completing anything.
      if (fallbackEmail) {
        setEmail(fallbackEmail);
      }

      if (!actionMode || !actionCode) {
        setStatus("error");
        setMessage("");
        setError("This email action link is incomplete or invalid.");
        return;
      }

      if (actionMode === "verifyEmail") {
        setMessage("Verifying your email address...");

        let verifiedEmail = "";

        try {
          // checkActionCode gives us the email bound to Firebase's signed
          // one-time code. This is authoritative and works on any device.
          const info = await checkActionCode(auth, actionCode);
          verifiedEmail = cleanEmail(info?.data?.email);

          if (!verifiedEmail) {
            throw new Error("Firebase did not return the email for this verification link.");
          }

          if (active) setEmail(verifiedEmail);

          // This globally marks the Firebase Auth account's email as verified.
          await applyActionCode(auth, actionCode);
        } catch (firebaseError) {
          // Recovery path for a link that was already successfully consumed,
          // but the subsequent AskLawly backend completion call failed or the
          // browser was refreshed. The backend remains authoritative: it will
          // only promote the pending signup if Firebase now reports
          // email_verified == true.
          if (fallbackEmail) {
            try {
              if (active) {
                setEmail(fallbackEmail);
                setMessage("Finishing your verified signup...");
              }

              await completeAskLawlySignup(fallbackEmail);

              try {
                localStorage.removeItem("signup_email");
              } catch (_storageError) {
                // Cross-device flow must never depend on localStorage.
              }

              if (!active) return;
              setStatus("success");
              setMessage("Your email is verified and your account is ready.");
              setError("");
              return;
            } catch (completionError) {
              // If Firebase still says the account is not verified, preserve
              // the Firebase action-code error because the recovery condition
              // was not satisfied.
              if (completionError?.status !== 400) {
                console.error(
                  "❌ Signup completion recovery failed:",
                  completionError
                );
              }
            }
          }

          console.error("❌ Firebase email verification failed:", firebaseError);
          if (!active) return;
          setStatus("error");
          setMessage("");
          setError(
            firebaseActionErrorMessage(
              firebaseError,
              "We could not verify this email link. Please request a new verification email."
            )
          );
          return;
        }

        if (!active) return;
        setMessage("Email verified. Completing your AskLawly account...");

        try {
          await completeAskLawlySignup(verifiedEmail);

          try {
            localStorage.removeItem("signup_email");
          } catch (_storageError) {
            // Cross-device flow must never depend on localStorage.
          }

          if (!active) return;
          setStatus("success");
          setMessage("Your email is verified and your account is ready.");
          setError("");
        } catch (completionError) {
          console.error("❌ AskLawly signup completion failed:", completionError);
          if (!active) return;
          setStatus("error");
          setMessage("");
          setError(
            completionError?.message ||
              "Your email was verified, but we could not finish creating your account. Please retry this link or contact support."
          );
        }

        return;
      }

      if (actionMode === "resetPassword") {
        setMessage("Checking your password reset link...");

        try {
          const resetEmail = cleanEmail(
            await verifyPasswordResetCode(auth, actionCode)
          );

          if (!active) return;
          setEmail(resetEmail);
          setStatus("reset-ready");
          setMessage("Enter a new password for your account.");
          setError("");
        } catch (resetError) {
          console.error("❌ Password reset code validation failed:", resetError);
          if (!active) return;
          setStatus("error");
          setMessage("");
          setError(
            firebaseActionErrorMessage(
              resetError,
              "This password reset link could not be validated. Please request a new one."
            )
          );
        }

        return;
      }

      // We are intentionally supporting only the Firebase email actions AskLawly
      // currently uses. Additional modes can be added here later without
      // changing the Firebase Console custom Action URL.
      setStatus("error");
      setMessage("");
      setError("This type of email action is not supported yet.");
    };

    run();

    return () => {
      active = false;
    };
  }, [auth]);

  const handlePasswordReset = async (event) => {
    event.preventDefault();

    if (!oobCode) {
      setError("The password reset code is missing.");
      return;
    }

    if (!newPassword) {
      setError("Please enter a new password.");
      return;
    }

    if (!isStrongPassword(newPassword)) {
      setError(
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character."
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }

    setResetSubmitting(true);
    setError("");

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus("reset-success");
      setMessage("Your password has been reset successfully. You can now sign in.");
      setNewPassword("");
      setConfirmPassword("");
    } catch (resetError) {
      console.error("❌ Password reset failed:", resetError);
      setError(
        firebaseActionErrorMessage(
          resetError,
          "We could not reset your password. Please request a new reset link and try again."
        )
      );
    } finally {
      setResetSubmitting(false);
    }
  };

  const handleVerificationResend = async () => {
    if (verificationResending || !email) return;

    setVerificationResending(true);
    setError("");
    setMessage("");

    try {
      const data = await resendAskLawlyVerification(email);

      if (data?.already_verified === true) {
        setStatus("success");
        setMessage(
          data?.message ||
            "Your email is already verified and your account is ready."
        );
        return;
      }

      setStatus("verification-resent");
      setMessage(
        data?.message ||
          `A new verification link has been sent to ${email}.`
      );
    } catch (resendError) {
      console.error("❌ Verification resend failed:", resendError);
      setStatus("error");
      setError(
        resendError?.message ||
          "We could not send a new verification email. Please try again."
      );
    } finally {
      setVerificationResending(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>
          {mode === "resetPassword" ? "Reset your password" : "Account verification"}
        </h2>

        {email && status !== "loading" && (
          <p style={styles.emailText}>
            Account: <strong>{email}</strong>
          </p>
        )}

        {status === "loading" && <p style={styles.text}>{message}</p>}

        {status === "success" && (
          <>
            <p style={styles.success}>{message}</p>
            <Link to="/login" style={styles.primaryLink}>
              Continue to sign in
            </Link>
          </>
        )}

        {status === "reset-ready" && (
          <form onSubmit={handlePasswordReset} style={styles.form}>
            <p style={styles.text}>{message}</p>

            <label style={styles.label} htmlFor="new-password">
              New password
            </label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              style={styles.input}
              disabled={resetSubmitting}
            />

            {newPassword && (
              <p style={styles.passwordHelp}>
                Password must be at least 8 characters and include uppercase,
                lowercase, number, and special character.
              </p>
            )}

            <label style={styles.label} htmlFor="confirm-password">
              Confirm new password
            </label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              style={styles.input}
              disabled={resetSubmitting}
            />

            {error && <p style={styles.error}>{error}</p>}

            <button
              type="submit"
              disabled={resetSubmitting}
              style={{
                ...styles.button,
                ...(resetSubmitting ? styles.buttonDisabled : {}),
              }}
            >
              {resetSubmitting ? "Resetting..." : "Reset password"}
            </button>
          </form>
        )}

        {status === "reset-success" && (
          <>
            <p style={styles.success}>{message}</p>
            <Link to="/login" style={styles.primaryLink}>
              Continue to sign in
            </Link>
          </>
        )}

        {status === "verification-resent" && (
          <>
            <p style={styles.success}>{message}</p>
            <p style={styles.text}>
              Please open the newest verification email. Older verification
              links may no longer work.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <p style={styles.error}>{error}</p>
            <div style={styles.linkRow}>
              {mode === "resetPassword" ? (
                <Link to="/forgot-password" style={styles.secondaryLink}>
                  Request a new reset link
                </Link>
              ) : email ? (
                <button
                  type="button"
                  onClick={handleVerificationResend}
                  disabled={verificationResending}
                  style={{
                    ...styles.secondaryButton,
                    ...(verificationResending ? styles.buttonDisabled : {}),
                  }}
                >
                  {verificationResending
                    ? "Sending..."
                    : "Send a new verification link"}
                </button>
              ) : (
                <Link to="/signup" style={styles.secondaryLink}>
                  Return to signup
                </Link>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "60vh",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "70px 20px 40px",
  },
  card: {
    width: "100%",
    maxWidth: "520px",
    border: "1px solid #e3e6ef",
    borderRadius: "12px",
    padding: "30px",
    backgroundColor: "#fff",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.06)",
  },
  title: {
    margin: "0 0 16px",
    fontSize: "26px",
    fontWeight: 700,
    textAlign: "center",
  },
  text: {
    margin: "0 0 18px",
    color: "#444",
    fontSize: "16px",
    lineHeight: 1.5,
    textAlign: "center",
  },
  emailText: {
    margin: "0 0 18px",
    color: "#555",
    fontSize: "14px",
    textAlign: "center",
    wordBreak: "break-word",
  },
  success: {
    margin: "0 0 22px",
    color: "#167c3b",
    fontSize: "16px",
    lineHeight: 1.5,
    textAlign: "center",
  },
  error: {
    margin: "0 0 18px",
    color: "#b42318",
    fontSize: "15px",
    lineHeight: 1.5,
    textAlign: "center",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  label: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#333",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 13px",
    border: "1px solid #cfd4df",
    borderRadius: "8px",
    fontSize: "16px",
    marginBottom: "6px",
  },
  passwordHelp: {
    margin: "-2px 0 6px",
    color: "#555",
    fontSize: "13px",
    lineHeight: 1.4,
  },
  button: {
    width: "100%",
    marginTop: "8px",
    padding: "12px 18px",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#1E2A78",
    color: "#fff",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
  },
  buttonDisabled: {
    opacity: 0.65,
    cursor: "default",
  },
  primaryLink: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 18px",
    borderRadius: "8px",
    backgroundColor: "#1E2A78",
    color: "#fff",
    textDecoration: "none",
    textAlign: "center",
    fontSize: "15px",
    fontWeight: 600,
  },
  secondaryLink: {
    color: "#1E2A78",
    textDecoration: "none",
    fontWeight: 600,
  },
  secondaryButton: {
    border: "none",
    background: "none",
    color: "#1E2A78",
    textDecoration: "underline",
    fontWeight: 600,
    fontSize: "15px",
    cursor: "pointer",
    padding: 0,
  },
  linkRow: {
    textAlign: "center",
  },
};

export default AuthAction;
